<template>
    <div class="shipment_record">
        <!-- 头部 -->
        <div class="list_header">
            <div class="back_btn" @click="$router.back()">
                <van-icon name="arrow-left" size="24" />
            </div>
            <span class="header_title">发货记录</span>
            <div class="filter_btn" @click="showFilter = true">
                <van-icon name="filter-o" size="24" />
            </div>
        </div>

        <!-- 列表 -->
        <div class="list_content">
            <van-tabs v-model="activeTab" sticky @change="onTabChange">
                <van-tab title="全部" name="all"></van-tab>
                <van-tab title="产品" name="product"></van-tab>
                <van-tab title="物料" name="material"></van-tab>
                <van-tab title="其他" name="other"></van-tab>
            </van-tabs>

            <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
                <van-list v-model="loading" :finished="finished" finished-text="没有更多了" @load="onLoad">
                    <div class="shipment_list">
                        <div class="shipment_item" v-for="item in list" :key="item.id" :class="{recently_viewed: isRecentlyViewed(item.id)}" @click="showDetail(item)">
                            <div class="rv_badge" v-if="isRecentlyViewed(item.id)">
                                <van-icon name="eye-o" size="12" />
                                <span>刚看过</span>
                            </div>
                            <div class="item_header">
                                <div class="header_left">
                                    <van-tag :type="getShipmentTypeColor(item.shipment_type)">
                                        {{ getShipmentTypeText(item.shipment_type) }}
                                    </van-tag>
                                    <span class="shipment_no">#{{ item.id }}</span>
                                </div>
                                <span class="shipment_time">{{ item.create_time | formatTime }}</span>
                            </div>

                            <div class="item_body">
                                <div class="goods_info">
                                    <div class="info_text">
                                        <span class="name">{{ item.intro || item.record_name || '-' }}</span>
                                        <span class="code">发货数量: {{ item.shipment_num || 0 }}件</span>
                                    </div>
                                </div>
                            </div>

                            <!-- 当前步骤状态 -->
                            <div class="current_step" :class="'step' + getCurrentStep(item)">
                                <van-icon name="clock" v-if="getCurrentStep(item) < 5" />
                                <van-icon name="checked" v-else />
                                <span>{{ getStepText(item) }}</span>
                                <van-button v-if="getCurrentStep(item) < 5 && canConfirmStep(getCurrentStep(item))"
                                    size="small" type="primary" @click.stop="confirmItem(item)"
                                    style="margin-left: 12px;">
                                    确认
                                </van-button>
                            </div>

                            <div class="item_footer">
                                <div class="sale_info">
                                    <van-icon name="user-o" size="14" />
                                    <span>销售: {{ item.sale_user_name || '-' }}</span>
                                </div>
                                <div class="receiver_info" v-if="item.shipment_address_info">
                                    <van-icon name="location-o" size="14" />
                                    <span>{{ formatAddress(item.shipment_address_info) }}</span>
                                </div>
                            </div>

                            <!-- 操作按钮 -->
                            <div class="item_actions">
                                <van-button size="small" @click.stop="editItem(item)">编辑</van-button>
                                <van-button size="small" type="danger" @click.stop="deleteItem(item)">删除</van-button>
                            </div>
                        </div>
                    </div>
                </van-list>
            </van-pull-refresh>
        </div>

        <!-- 筛选弹窗 -->
        <van-popup v-model="showFilter" position="right" style="width: 80%; height: 100%;">
            <div class="filter_panel">
                <div class="filter_header">
                    <span>筛选条件</span>
                    <van-icon name="cross" size="24" @click="showFilter = false" />
                </div>

                <div class="filter_body">
                    <div class="filter_section">
                        <div class="filter_title">应用</div>
                        <van-field v-model="filter.studio_name" type="select" placeholder="请选择应用" readonly
                            @click="showStudioSelect = true" class="filter_field">
                            <template v-if="filter.studio_name">#{{ filter.studio_id }} {{ filter.studio_name }}</template>
                        </van-field>
                    </div>

                    <div class="filter_section">
                        <div class="filter_title">销售人员</div>
                        <van-field v-model="filter.sale_user_name" type="select" placeholder="请选择销售人员" readonly
                            @click="showSaleUserSelect = true" class="filter_field">
                            <template v-if="filter.sale_user_name">{{ filter.sale_user_name }}</template>
                        </van-field>
                    </div>

                    <div class="filter_section">
                        <div class="filter_title">确认用户</div>
                        <van-field v-model="filter.confirm_user_name" type="select" placeholder="请选择确认用户" readonly
                            @click="showConfirmUserSelect = true" class="filter_field">
                            <template v-if="filter.confirm_user_name">{{ filter.confirm_user_name }}</template>
                        </van-field>
                    </div>
                </div>

                <div class="filter_footer">
                    <van-button type="default" block @click="resetFilter">重置</van-button>
                    <van-button type="primary" block @click="confirmFilter">确定</van-button>
                </div>
            </div>
        </van-popup>

        <!-- 应用选择器 -->
        <van-popup v-model="showStudioSelect" position="bottom" round>
            <van-picker ref="studioPicker" show-toolbar :columns="studioList" value-key="text"
                @confirm="onStudioConfirm" @cancel="showStudioSelect = false" />
        </van-popup>

        <!-- 销售人员选择器 -->
        <van-popup v-model="showSaleUserSelect" position="bottom" round>
            <van-picker ref="saleUserPicker" show-toolbar :columns="saleUserList" value-key="text"
                @confirm="onSaleUserConfirm" @cancel="showSaleUserSelect = false" />
        </van-popup>

        <!-- 确认用户选择器 -->
        <van-popup v-model="showConfirmUserSelect" position="bottom" round>
            <van-picker ref="confirmUserPicker" show-toolbar :columns="confirmUserList" value-key="text"
                @confirm="onConfirmUserConfirm" @cancel="showConfirmUserSelect = false" />
        </van-popup>

        <!-- 新增按钮 -->
        <div class="add_btn" @click="openShipmentDialog">
            <van-icon name="plus" color="#fff" size="28" />
        </div>

        <!-- 发货弹窗 -->
        <shipment-dialog
            v-model="showShipmentDialog"
            shipment-type="product"
            :user-info="userInfo"
            @success="onShipmentSuccess"
        />

    </div>
</template>

<script>
import studio_api from '@/api/teaching_studio_teaching_studio_api.js'
import studio_tool_api from '@/api/teaching_studio_tool.js'
import until from '@/api/until.js'
import ShipmentDialog from '@/components/ShipmentDialog.vue'

export default {
    name: 'ShipmentRecord',
    components: { ShipmentDialog },
    data() {
        return {
            activeTab: 'all',
            list: [],
            page: 1,
            each_page: 10,
            loading: false,
            finished: false,
            refreshing: false,
            isFetching: false,
            showFilter: false,

            showShipmentDialog: false,
            userInfo: {},

            // 选择器显示状态
            showStudioSelect: false,
            showSaleUserSelect: false,
            showConfirmUserSelect: false,

            // 选项列表
            studioList: [],
            saleUserList: [],
            confirmUserList: [],

            filter: {
                studio_id: '',
                studio_name: '',
                sale_user_id: '',
                sale_user_name: '',
                confirm_user_id: '',
                confirm_user_name: ''
            }
        }
    },
    filters: {
        formatTime(timestamp) {
            if (!timestamp) return '-'
            let date = new Date(timestamp * 1000)
            return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
        },
        getFirstImg(imgs) {
            if (!imgs) return '/static/imgs/no_img.png'
            return imgs
        }
    },
    mounted() {
        document.title = "发货记录"
        this.getFilterOptions()
        this.getUserInfo()
    },
    methods: {
        // 打开发货弹窗
        openShipmentDialog() {
            this.showShipmentDialog = true
        },
        // 发货成功回调
        onShipmentSuccess() {
            this.onRefresh()
        },
        // 获取用户信息
        async getUserInfo() {
            try {
                let data = await studio_api.Teaching_studio_apiMemberLogin_userGet({ app_id: this.$root.app_id })
                if (data.errcode == 0) {
                    this.userInfo = data.other
                }
            } catch(e) {}
        },
        // 获取发货类型文本
        getShipmentTypeText(type) {
            const texts = {
                product: '产品',
                material: '物料',
                other: '其他'
            }
            return texts[type] || '-'
        },
        // 获取发货类型标签颜色
        getShipmentTypeColor(type) {
            const colors = {
                product: 'primary',
                material: 'success',
                other: 'warning'
            }
            return colors[type] || 'default'
        },
        // 获取筛选选项列表
        async getFilterOptions() {
            this.getStudioList()
            this.getSaleUserList()
            this.getConfirmUserList()
        },

        // 获取应用列表
        async getStudioList() {
            try {
                let res = await studio_api.Teaching_studio_apiStudioWechatStudioGet({
                    app_id: this.$root.app_id
                })
                if (res.errcode === 0 && res.other) {
                    // res.other 可能是数组或 {list: [], total: 0} 格式
                    let listData = Array.isArray(res.other) ? res.other : (res.other.list || [])
                    this.studioList = listData.map(item => ({
                        text: `#${item.id} ${item.name}`,
                        value: String(item.id)
                    }))
                }
            } catch (e) {
                console.error('获取应用列表失败', e)
            }
        },

        // 获取销售人员列表
        async getSaleUserList() {
            try {
                let res = await studio_api.Teaching_studio_apiIotmaxUsersGet({
                    app_id: this.$root.app_id
                })
                if (res.errcode === 0 && res.other) {
                    // res.other 格式：{list: [...], total: 9}
                    let listData = res.other.list || []
                    this.saleUserList = listData.map(item => ({
                        text: item.real_name || item.name,
                        value: String(item.user_id || item.id)
                    }))
                    // 确认用户和销售人员是同一批人
                    this.confirmUserList = [...this.saleUserList]
                }
            } catch (e) {
                console.error('获取用户列表失败', e)
            }
        },

        // 获取确认用户列表（与销售人员相同）
        async getConfirmUserList() {
            // 已经在 getSaleUserList 中一起获取了
        },

        // 应用选择确认
        onStudioConfirm() {
            const picker = this.$refs.studioPicker
            if (picker) {
                const values = picker.getValues()
                console.log('应用选择 values:', values)
                const item = values[0]
                if (item) {
                    this.filter.studio_id = item.value
                    this.filter.studio_name = item.text.replace(/^#\d+\s*/, '')
                }
            }
            this.showStudioSelect = false
        },

        // 销售人员选择确认
        onSaleUserConfirm() {
            const picker = this.$refs.saleUserPicker
            if (picker) {
                const values = picker.getValues()
                console.log('销售人员选择 values:', values)
                const item = values[0]
                if (item) {
                    this.filter.sale_user_id = item.value
                    this.filter.sale_user_name = item.text
                }
            }
            this.showSaleUserSelect = false
        },

        // 确认用户选择确认
        onConfirmUserConfirm() {
            const picker = this.$refs.confirmUserPicker
            if (picker) {
                const values = picker.getValues()
                console.log('确认用户选择 values:', values)
                const item = values[0]
                if (item) {
                    this.filter.confirm_user_id = item.value
                    this.filter.confirm_user_name = item.text
                }
            }
            this.showConfirmUserSelect = false
        },
        // 获取列表
        async getList() {
            if (this.isFetching) return // 防止重复请求

            this.isFetching = true

            let params = {
                page: this.page,
                each_page: this.each_page
            }

            // 类型筛选
            if (this.activeTab !== 'all') {
                params.shipment_type = this.activeTab
            }

            // 应用筛选
            if (this.filter.studio_id) {
                params.studio_id = this.filter.studio_id
            }

            // 销售人员筛选
            if (this.filter.sale_user_id) {
                params.sale_user_id = this.filter.sale_user_id
            }

            try {
                let data = await studio_api.Teaching_studio_apiIotmaxShipmentListGet({ ...params, app_id: this.$root.app_id })

                // 确保无论成功失败，在处理数据前先把 isFetching 置为 false
                // 以免阻塞下一次触发
                this.isFetching = false

                if (data.errcode === 0) {
                    // API返回格式: {count: 3, list: [...]}
                    let originalList = data.other && data.other.list ? data.other.list : []

                    if (this.page === 1) {
                        this.list = [...originalList]
                    } else {
                        // 去重
                        let newItems = originalList.filter(item => !this.list.some(existing => existing.id === item.id))
                        this.list = this.list.concat(newItems)
                    }

                    // 使用新请求到的数据长度判断是否结束
                    // 并且要等渲染完后再结束 loading 状态
                    // 【注意】如果接口返回的列表长度小于 each_page，说明没数据了
                    if (originalList.length < this.each_page) {
                        this.finished = true
                    } else {
                        // 只有真正拿到一整页数据，才把页码加1
                        this.page++
                    }
                } else {
                    this.finished = true
                }
            } catch (error) {
                console.error('获取列表失败', error)
                this.finished = true
            } finally {
                // 等待 Vue 将新的 list 渲染到 DOM 上之后，再重置加载状态
                this.$nextTick(() => {
                    this.loading = false
                    this.refreshing = false
                    this.isFetching = false
                })
            }
        },

        // 刷新
        onRefresh() {
            this.page = 1
            this.list = []
            this.finished = false
            // 只有手动下拉刷新和重置才需要手动触发加载
            // 其他情况交由 van-list 的 onLoad 触发
            this.getList()
        },

        // 加载更多
        onLoad() {
            if (this.finished || this.isFetching) {
                this.loading = false
                return
            }
            this.getList()
        },

        // Tab切换
        onTabChange() {
            this.page = 1
            this.list = []
            this.finished = false
            this.getList()
        },

        // 重置筛选
        resetFilter() {
            this.filter = {
                studio_id: '',
                studio_name: '',
                sale_user_id: '',
                sale_user_name: '',
                confirm_user_id: '',
                confirm_user_name: ''
            }
        },

        // 确认筛选
        confirmFilter() {
            this.showFilter = false
            this.onRefresh()
        },

        // 编辑项目
        editItem(item) {
            this.$router.push({
                path: '/warehouse/shipment/add',
                query: { id: item.id, action: 'edit' }
            })
        },

        // 删除项目
        deleteItem(item) {
            this.$dialog.confirm({
                title: '确认删除',
                message: '确定要删除这条发货记录吗？'
            }).then(async () => {
                this.$toast.loading({ message: '删除中...', duration: 0 })

                try {
                    let res = await studio_api.Teaching_studio_apiIotmaxShipmentDelDelete({
                        app_id: this.$root.app_id,
                        id: item.id
                    })

                    this.$toast.clear()

                    if (res.errcode === 0) {
                        this.$toast.success('删除成功')
                        // 刷新列表
                        this.onRefresh()
                    } else {
                        this.$toast(res.errmsg || '删除失败')
                    }
                } catch (e) {
                    this.$toast.clear()
                    console.error('删除失败', e)
                    this.$toast('删除失败')
                }
            }).catch(() => {
                // 取消删除
            })
        },

        // 判断是否有确认权限
        canConfirm() {
            // 超级管理员权限
            if (this.$root.isSuperRoot) {
                return true
            }
            // 检查用户权限
            if (this.$root.userInfo && this.$root.userInfo.stock_user_role) {
                const roleType = this.$root.userInfo.stock_user_role.role_type || ''
                const roles = roleType.split(',')
                // 检查是否包含任意一种确认权限
                return roles.includes('debug') || roles.includes('need') || roles.includes('quality') || roles.includes('shipment')
            }
            return false
        },

        // 判断是否有对应步骤的确认权限
        canConfirmStep(step) {
            // 超级管理员权限
            if (this.$root.isSuperRoot) {
                return true
            }
            // 检查用户权限
            if (this.$root.userInfo && this.$root.userInfo.stock_user_role) {
                const roleType = this.$root.userInfo.stock_user_role.role_type || ''
                const roles = roleType.split(',')
                // 根据步骤检查对应的权限
                if (step === 1) {
                    return roles.includes('debug')
                } else if (step === 2) {
                    return roles.includes('need')
                } else if (step === 3) {
                    return roles.includes('quality')
                } else if (step === 4) {
                    return roles.includes('shipment')
                }
            }
            return false
        },

        // 确认项目
        async confirmItem(item) {
            const step = this.getCurrentStep(item)
            const stepNames = {
                1: '调试确认',
                2: '需求确认',
                3: '质量确认',
                4: '发货确认'
            }

            try {
                await this.$dialog.confirm({
                    title: '确认操作',
                    message: `确定要${stepNames[step]}吗？`
                })
            } catch (e) {
                return
            }

            this.$toast.loading({ message: '确认中...', duration: 0 })

            try {
                // 获取 computer_code 和 salt
                const computer_code = until.getCookie("guid").substring(0, 6)

                let saltRes = await studio_tool_api.ToolForm_saltGet({
                    computer_code: computer_code
                })

                if (saltRes.errcode !== 0 || !saltRes.other) {
                    this.$toast.clear()
                    this.$toast('获取 salt 失败')
                    return
                }

                const salt = saltRes.other

                // 调用对应的确认接口
                let res
                if (step === 1) {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentDebug_surePut({
                        app_id: this.$root.app_id,
                        computer_code: computer_code,
                        salt: salt,
                        id: item.id
                    })
                } else if (step === 2) {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentNeed_surePut({
                        app_id: this.$root.app_id,
                        computer_code: computer_code,
                        salt: salt,
                        id: item.id
                    })
                } else if (step === 3) {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentQuality_surePut({
                        app_id: this.$root.app_id,
                        computer_code: computer_code,
                        salt: salt,
                        id: item.id
                    })
                } else if (step === 4) {
                    res = await studio_api.Teaching_studio_apiIotmaxShipmentShipment_surePut({
                        app_id: this.$root.app_id,
                        computer_code: computer_code,
                        salt: salt,
                        id: item.id
                    })
                }

                this.$toast.clear()

                if (res.errcode === 0) {
                    this.$toast.success('确认成功')
                    // 刷新列表
                    this.onRefresh()
                } else {
                    this.$toast(res.errmsg || '确认失败')
                }
            } catch (e) {
                this.$toast.clear()
                console.error('确认失败', e)
                this.$toast('确认失败')
            }
        },

        // 显示详情 - 跳转到详情页
        showDetail(item) {
            sessionStorage.setItem('last_viewed_shipment', item.id)
            this.$router.push({
                path: '/warehouse/shipment/detail',
                query: { id: item.id }
            })
        },

        isRecentlyViewed(id) {
            return sessionStorage.getItem('last_viewed_shipment') == id
        },

        // 格式化地址显示（列表用，简略版）
        formatAddress(addressInfo) {
            if (!addressInfo) return '-'
            // 如果不是JSON格式，直接返回
            if (!addressInfo.includes('{')) {
                return addressInfo.length > 30 ? addressInfo.substring(0, 30) + '...' : addressInfo
            }
            try {
                let info = JSON.parse(addressInfo)
                let result = info.name || info.receiver_name || ''
                if (info.phone || info.tel) {
                    result += ' ' + (info.phone || info.tel)
                }
                if (info.address) {
                    result += ' ' + info.address
                }
                return result.length > 35 ? result.substring(0, 35) + '...' : result
            } catch (e) {
                return addressInfo.length > 30 ? addressInfo.substring(0, 30) + '...' : addressInfo
            }
        },

        // 解析地址信息（详情用）
        // 支持格式1: JSON {"name":"xxx","phone":"xxx","address":"xxx"}
        // 支持格式2: "姓名，电话，地址" 中文逗号分隔
        parseAddressInfo(addressInfo) {
            if (!addressInfo) return null

            // 尝试JSON解析
            if (addressInfo.includes('{')) {
                try {
                    let info = JSON.parse(addressInfo)
                    return {
                        name: info.name || info.receiver_name || '',
                        phone: info.phone || info.tel || '',
                        address: info.address || ''
                    }
                } catch (e) {
                    // JSON解析失败，继续尝试其他格式
                }
            }

            // 尝试中文逗号分隔格式: "姓名，电话，地址"
            // 支持多个逗号的情况，前两个分别是姓名和电话，剩下的合并为地址
            const parts = addressInfo.split('，')
            if (parts.length >= 3) {
                return {
                    name: parts[0].trim(),
                    phone: parts[1].trim(),
                    address: parts.slice(2).join('，').trim()
                }
            }

            // 尝试英文逗号分隔
            const enParts = addressInfo.split(',')
            if (enParts.length >= 3) {
                return {
                    name: enParts[0].trim(),
                    phone: enParts[1].trim(),
                    address: enParts.slice(2).join(',').trim()
                }
            }

            // 无法解析，全部作为地址
            return { name: '', phone: '', address: addressInfo }
        },

        // 获取当前步骤 1-5
        getCurrentStep(item) {
            if (!item.debug_sure_user_id) return 1
            if (!item.need_sure_user_id) return 2
            if (!item.quality_sure_user_id) return 3
            if (!item.shipment_sure_user_id) return 4
            return 5
        },

        // 获取步骤文本
        getStepText(item) {
            const step = this.getCurrentStep(item)
            const texts = {
                1: '待调试',
                2: '待需求确认',
                3: '待质量确认',
                4: '待发货确认',
                5: '已完成'
            }
            return texts[step]
        }
    }
}
</script>

<style scoped>
.shipment_record {
    width: 100%;
    height: 100vh;
    overflow-y: auto;
    background: #F6F6F6;
}

.list_header {
    width: 100%;
    height: 88px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 30px;
    box-sizing: border-box;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.back_btn i,
.filter_btn i {
    font-size: 40px;
    color: #333;
}

.header_title {
    font-size: 32px;
    font-weight: bold;
    color: #333;
}

.list_content {
    background: #fff;
    border-radius: 20px 20px 0 0;
    min-height: calc(100vh - 300px);
}

.shipment_list {
    padding: 20px 30px;
}

.shipment_item {
    padding: 24px;
    background: #fff;
    border-radius: 16px;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.item_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.header_left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.shipment_no {
    font-size: 26px;
    color: #666;
}

.shipment_time {
    font-size: 24px;
    color: #999;
}

.item_body {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
}

.goods_info {
    display: flex;
    align-items: center;
    flex: 1;
}

.goods_info img {
    width: 100px;
    height: 100px;
    border-radius: 12px;
    object-fit: cover;
    margin-right: 20px;
}

.info_text {
    display: flex;
    flex-direction: column;
}

.info_text .name {
    font-size: 30px;
    color: #333;
    font-weight: bold;
}

.info_text .code {
    font-size: 24px;
    color: #999;
    margin-top: 8px;
}

.shipment_num {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.shipment_num .num {
    font-size: 40px;
    font-weight: bold;
    color: #F44336;
}

.shipment_num .unit {
    font-size: 24px;
    color: #999;
}

.item_footer {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #f0f0f0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.item_actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.sale_info,
.receiver_info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 24px;
    color: #666;
}

/* 当前步骤状态 */
.current_step {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 26px;
    margin: 16px 0;
}

/* 步骤颜色统一规范 - 与 step_badge 保持一致 */
/* 步骤1-待调试：灰色（未开始） */
.current_step.step1 {
    background: #F5F5F5;
    color: #666;
}

/* 步骤2-待需求确认：橙色（进行中） */
.current_step.step2 {
    background: #FFF3E0;
    color: #FF9800;
}

/* 步骤3-待质量确认：蓝色（进行中） */
.current_step.step3 {
    background: #E3F2FD;
    color: #2196F3;
}

/* 步骤4-待发货确认：青色（即将完成） */
.current_step.step4 {
    background: #E0F7FA;
    color: #00BCD4;
}

/* 步骤5-已完成：绿色 */
.current_step.step5 {
    background: #4CAF50;
    color: #fff;
}

/* 筛选面板 */
.filter_panel {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.filter_header {
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 30px;
    border-bottom: 1px solid #f0f0f0;
}

.filter_header span {
    font-size: 32px;
    font-weight: bold;
    color: #333;
}

.filter_header i {
    font-size: 36px;
    color: #999;
}

.filter_body {
    flex: 1;
    padding: 30px;
    overflow-y: auto;
}

.filter_section {
    margin-bottom: 40px;
}

.filter_title {
    font-size: 28px;
    color: #333;
    font-weight: bold;
    margin-bottom: 20px;
}

.filter_options {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}

.option {
    padding: 16px 40px;
    background: #f5f5f5;
    border-radius: 8px;
    font-size: 26px;
    color: #666;
}

.option.active {
    background: #E3F2FD;
    color: #2196F3;
}

.date_range {
    display: flex;
    align-items: center;
    gap: 20px;
}

.date_input {
    flex: 1;
    height: 72px;
    background: #f5f5f5;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    color: #666;
}

.filter_field {
    background: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 20px;
}

.filter_footer {
    padding: 20px 30px;
    display: flex;
    gap: 20px;
    border-top: 1px solid #f0f0f0;
}

/* 详情弹窗 */
.detail_popup {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.popup_header {
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 30px;
    border-bottom: 1px solid #f0f0f0;
}

.popup_header span {
    font-size: 32px;
    font-weight: bold;
    color: #333;
}

.popup_header i {
    font-size: 36px;
    color: #999;
}

.popup_body {
    flex: 1;
    padding: 30px;
    overflow-y: auto;
}

.detail_section {
    margin-bottom: 30px;
}

.section_title {
    font-size: 28px;
    font-weight: bold;
    color: #333;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 2px solid #4E89FF;
}

.detail_row .value.completed {
    color: #4CAF50;
}

.detail_row .value .van-icon {
    margin-right: 8px;
}

/* 流程列表 */
.section_title {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.step_badge {
    padding: 6px 16px;
    border-radius: 6px;
    font-size: 24px;
}

/* 步骤颜色统一规范 */
/* 步骤1-待调试：灰色（未开始） */
.step_badge.step1 {
    background: #F5F5F5;
    color: #999;
}

/* 步骤2-待需求确认：橙色（进行中） */
.step_badge.step2 {
    background: #FFF3E0;
    color: #FF9800;
}

/* 步骤3-待质量确认：蓝色（进行中） */
.step_badge.step3 {
    background: #E3F2FD;
    color: #2196F3;
}

/* 步骤4-待发货确认：青色（即将完成） */
.step_badge.step4 {
    background: #E0F7FA;
    color: #00BCD4;
}

/* 步骤5-已完成：绿色 */
.step_badge.step5 {
    background: #4CAF50;
    color: #fff;
}

.flow_list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.flow_item {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 20px;
    background: #f9f9f9;
    border-radius: 12px;
    border-left: 4px solid #ddd;
}

/* 当前进行中的步骤 - 橙色高亮 */
.flow_item.active {
    background: #FFF8E1;
    border-left-color: #FF9800;
}

/* 已完成的步骤 - 绿色 */
.flow_item.completed {
    background: #E8F5E9;
    border-left-color: #4CAF50;
}

/* 确保已完成步骤的文字颜色正确 */
.flow_item.completed .flow_name {
    color: #2E7D32;
}

.flow_icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: #999;
    flex-shrink: 0;
}

.flow_item.active .flow_icon {
    background: #FF9800;
    color: #fff;
}

.flow_item.completed .flow_icon {
    background: #4CAF50;
    color: #fff;
}

.flow_info {
    flex: 1;
}

.flow_name {
    font-size: 28px;
    color: #333;
    font-weight: bold;
}

.flow_user {
    font-size: 24px;
    color: #4CAF50;
    margin-top: 6px;
}

.flow_pending {
    font-size: 24px;
    color: #999;
    margin-top: 6px;
}

.remark_text {
    font-size: 28px;
    color: #666;
    line-height: 1.6;
    padding: 20px;
    background: #f9f9f9;
    border-radius: 8px;
}

.detail_row {
    display: flex;
    align-items: center;
    padding: 24px 0;
    border-bottom: 1px solid #f5f5f5;
}

.detail_row .label {
    width: 160px;
    font-size: 28px;
    color: #999;
    flex-shrink: 0;
}

.detail_row .value {
    flex: 1;
    font-size: 28px;
    color: #333;
    word-break: break-all;
}

.detail_row .value.highlight {
    color: #F44336;
    font-weight: bold;
}

/* 新增按钮 */
.add_btn {
    position: fixed;
    right: 30px;
    bottom: 120px;
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background: #1989fa;
    box-shadow: 0 4px 12px rgba(25, 137, 250, 0.4);
    z-index: 99;
    display: flex;
    align-items: center;
    justify-content: center;
}

.add_btn:active {
    transform: scale(0.9);
    box-shadow: 0 2px 8px rgba(25, 137, 250, 0.3);
}

.recently_viewed {
    border-left: 8px solid #F44336;
    background: linear-gradient(135deg, rgba(244,67,54,0.06) 0%, rgba(244,67,54,0.01) 100%);
    box-shadow: 0 0 0 2px rgba(244,67,54,0.12), 0 4px 16px rgba(244,67,54,0.08);
    position: relative;
}

.rv_badge {
    position: absolute;
    top: -1px;
    right: 16px;
    background: linear-gradient(135deg, #F44336, #D32F2F);
    color: #fff;
    font-size: 20px;
    padding: 4px 14px 6px;
    border-radius: 0 0 10px 10px;
    display: flex;
    align-items: center;
    gap: 4px;
    z-index: 2;
    box-shadow: 0 2px 8px rgba(244,67,54,0.3);
    animation: rvPulse 2s ease-in-out infinite;
}

@keyframes rvPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
}
</style>
